import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ScrollText, Swords, Gem, ArrowRight, BookOpen, ShieldCheck, Coins, Crown, PawPrint, Trophy, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { CREST_EMBLEM, RANK_BADGES } from "@/lib/assets";
import GemDivider from "@/components/GemDivider";
import { btnPrimary, btnSecondary, card, cardHover, tag, label, LEVEL_COLORS } from "@/lib/styles";

const TIER_NAME = ["Initiate", "Apprentice", "Adept", "Artificer", "Archmage"];

export default function Landing() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/curriculum/public").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <div className="min-h-screen">
      {/* HERO — centered, fantasy-academy */}
      <section className="relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-4 py-20 lg:py-28 text-center relative z-10">
          <motion.img
            src={CREST_EMBLEM} alt="" draggable={false}
            initial={{ opacity: 0, scale: 0.9, y: -10 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="w-28 h-28 lg:w-36 lg:h-36 mx-auto select-none pointer-events-none drop-shadow-[0_8px_30px_rgba(217,164,65,0.25)]"
          />
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05 }}
            className="mt-6 text-[12px] uppercase tracking-[0.4em] text-gold font-semibold"
          >
            Promptbound
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.6 }}
            className="mt-3 font-serif font-bold text-4xl sm:text-5xl lg:text-6xl text-zinc-100 leading-[1.05]"
          >
            Master AI engineering.<br />
            <span className="text-gold glow-gold italic">Then prove it.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
            className="mt-6 text-lg text-zinc-400 max-w-xl mx-auto leading-relaxed"
          >
            Every lesson free, forever: a 30-quest journey from "what is an LLM" to production agents.
            Then go Pro and face Altaris: questions drawn from real interview loops at Google,
            Meta, Amazon and the top AI labs, graded against a hidden rubric.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="mt-10 flex flex-wrap gap-4 justify-center"
          >
            <Link to="/register" data-testid="hero-start-studying" className={btnPrimary}>
              <BookOpen size={16} /> Begin the journey
            </Link>
            <Link to="/lesson" data-testid="hero-free-lesson" className={btnSecondary}>
              <ScrollText size={16} /> Read lesson one, no account
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
            className="mt-7 flex justify-center"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-950/30 px-4 py-1.5 text-xs font-semibold text-emerald-300">
              <Sparkles size={13} className="text-emerald-400" /> All 40 lessons 100% free forever · no credit card · 3 graded interviews on the house
            </span>
          </motion.div>
        </div>
      </section>

      <GemDivider />

      {/* PROVENANCE — questions from real, verified interview loops */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className={`${card} p-8 sm:p-10 text-center`}
        >
          <div className="inline-flex items-center gap-2 text-emerald-400">
            <ShieldCheck size={16} /><span className={label}>Verified, not invented</span>
          </div>
          <p className="mt-4 text-lg text-zinc-300 leading-relaxed max-w-2xl mx-auto">
            72 of Altaris's questions are adapted from <span className="text-gold">real 2024–2026 interview loops</span>,
            the actual screens engineers faced, mapped onto the course.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
            {["Meta", "Google", "Amazon", "OpenAI", "Anthropic"].map((c) => (
              <span key={c} className="font-serif text-xl sm:text-2xl font-bold text-zinc-400 tracking-tight">{c}</span>
            ))}
          </div>
          <p className="mt-5 text-xs text-zinc-600">Meta MLE / ML-design · Google GenAI · Amazon Applied Scientist · OpenAI &amp; Anthropic engineer loops</p>
        </motion.div>
      </section>

      <GemDivider />

      {/* THE GRADING LOOP */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <span className={label}>The differentiator</span>
          <h2 className="mt-3 font-serif font-bold text-3xl sm:text-4xl text-zinc-100">The grading loop</h2>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: <ScrollText className="text-gold" />, t: "Study the lore", d: "A structured curriculum with scope summaries, key concepts and flip-to-reveal flashcards for every topic." },
            { icon: <Swords className="text-gold" />, t: "Enter the arena", d: "Altaris asks one question at a time, drawn from real interview loops at Google, Meta, Amazon and the top AI labs. You answer free-form, like a real oral screen." },
            { icon: <Gem className="text-gem" />, t: "The hidden rubric", d: "Altaris privately holds the ideal answer and rubric. You never see it until after you're graded." },
          ].map((b, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className={`${card} ${cardHover} p-8 text-center`}>
              <div className="w-12 h-12 mx-auto rounded-full border border-gold/30 bg-gold/5 flex items-center justify-center mb-4">{b.icon}</div>
              <h3 className="font-serif font-bold text-2xl text-zinc-100 mb-2">{b.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{b.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <GemDivider />

      {/* RPG MECHANICS — not a course, a game */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <span className={label}>The twist</span>
          <h2 className="mt-3 font-serif font-bold text-3xl sm:text-4xl text-zinc-100">Not a course. A game.</h2>
          <p className="mt-3 text-sm text-zinc-500 max-w-lg mx-auto">Every quiz pays out. Bank the gold, climb the ranks, and spend it on things that fight back.</p>
        </div>
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: <Coins className="text-gold" />, t: "Gold & XP", d: "Clear quizzes and quests to bank gold and level up as you learn." },
            { icon: <Crown className="text-gold" />, t: "Five ranks", d: "Rise from Initiate to Archmage across the thirty-quest path." },
            { icon: <PawPrint className="text-gem" />, t: "A familiar", d: "Adopt an egg and feed it gold: it evolves from Spark to a true Oracle." },
            { icon: <Trophy className="text-gold" />, t: "The Gauntlet", d: "Wager gold on a timed trial; top the monthly board to claim the jackpot." },
          ].map((b, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.07 }} className={`${card} ${cardHover} p-6 text-center`}>
              <div className="w-11 h-11 mx-auto rounded-full border border-gold/30 bg-gold/5 flex items-center justify-center mb-3">{b.icon}</div>
              <h3 className="font-serif font-bold text-lg text-zinc-100 mb-1.5">{b.t}</h3>
              <p className="text-xs text-zinc-400 leading-relaxed">{b.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <GemDivider />

      {/* TIER LADDER */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="text-center">
          <span className={label}>The path</span>
          <h2 className="mt-3 font-serif font-bold text-3xl sm:text-4xl text-zinc-100">Five ranks, thirty quests</h2>
          <p className="mt-3 text-sm text-zinc-500 max-w-lg mx-auto">
            <Link to="/lesson" className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">Read the first one right now</Link>, no account needed. Thirty quests, plus ten optional deep dives on the theory: forty lessons, all free.
          </p>
        </div>

        <div className="mt-12 space-y-4">
          {data?.tiers?.map((tier, idx) => (
            <motion.div
              key={tier.tier}
              initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.05 }}
              className={`${card} ${cardHover} p-6 lg:p-7`}
              data-testid={`landing-tier-${tier.tier}`}
            >
              <div className="flex items-start gap-5">
                <div className="shrink-0 flex flex-col items-center gap-1.5 w-20">
                  <img src={RANK_BADGES[tier.tier]} alt="" className="w-16 h-16 select-none pointer-events-none drop-shadow-[0_4px_14px_rgba(217,164,65,0.25)]" style={{ WebkitMaskImage: "radial-gradient(circle,#000 62%,transparent 74%)", maskImage: "radial-gradient(circle,#000 62%,transparent 74%)" }} />
                  <span className="text-[10px] uppercase tracking-[0.15em] text-gold/80 font-semibold">Rank {tier.tier}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">{TIER_NAME[tier.tier]}</span>
                  <h3 className="font-serif font-bold text-xl text-zinc-100 mt-0.5">{tier.title}</h3>
                  <p className="text-xs text-zinc-500 mb-4">{tier.subtitle}</p>
                  <div className="flex flex-wrap gap-2">
                    {tier.topics.map((t) => (
                      <span key={t.topic_id} className={`${tag} gap-1.5`}>
                        <Lock size={10} className="text-zinc-600" /> {t.title}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 text-center border border-gold/25 rounded-xl bg-gradient-to-b from-[#141d34]/60 to-transparent p-12 gold-glow">
          <Gem className="text-gem mx-auto mb-3" />
          <h3 className="font-serif font-bold text-3xl text-zinc-100">Ready to take the oath?</h3>
          <p className="text-sm text-zinc-400 mt-2 mb-7 max-w-md mx-auto">Every lesson is free and your AI career won't start itself. Place yourself in 30 seconds and begin at the rank that fits you.</p>
          <Link to="/register" data-testid="landing-cta-signup" className={btnPrimary}>
            Create free account <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
